import React, { useState, useEffect } from "react";
import "./usermanagement.css";
import Loader from "../../../components/Loader";
import { API_BASE_URL, authFetch } from "../../../config/api";

const emptyForm = { name: "", email: "", role: "exam_officer" };

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const role = localStorage.getItem("role");
  const isSuperAdmin = role === "super_admin";
  const token = localStorage.getItem("token");
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/api/users`, { headers });
      const json = await res.json();
      if (json.success) setUsers(json.users);
    } catch {
      setMessage({ type: "error", text: "Failed to load users." });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      return setMessage({ type: "error", text: "All fields are required." });
    }
    setSubmitting(true);
    setMessage({ type: "", text: "" });
    try {
      const res = await authFetch(`${API_BASE_URL}/api/users`, {
        method: "POST",
        headers,
        body: JSON.stringify(form),
      });
      const json = await res.json();
      if (json.success) {
        setMessage({ type: "success", text: "User created successfully." });
        setForm(emptyForm);
        fetchUsers();
      } else {
        setMessage({
          type: "error",
          text: json.error || "Failed to create user.",
        });
      }
    } catch {
      setMessage({ type: "error", text: "Server error." });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (_id) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      const res = await authFetch(`${API_BASE_URL}/api/users/${_id}`, {
        method: "DELETE",
        headers,
      });
      const json = await res.json();
      if (json.success) {
        setMessage({ type: "success", text: "User deleted." });
        fetchUsers();
      } else {
        setMessage({
          type: "error",
          text: json.error || "Failed to delete user.",
        });
      }
    } catch {
      setMessage({ type: "error", text: "Server error." });
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  return (
    <div className="um_page">
      <div className="um_header">
        <h2>User Management</h2>
        <p>Create and manage Dean and Exam Officer accounts</p>
      </div>

      {message.text && (
        <div className={`um_message ${message.type}`}>
          {message.text}
          <button onClick={() => setMessage({ type: "", text: "" })}>×</button>
        </div>
      )}

      <div className="um_body">
        <div className="um_form_card">
          <h3>Create New User</h3>
          <form onSubmit={handleCreate} className="um_form">
            <div className="um_field">
              <label>Full Name</label>
              <input
                type="text"
                placeholder="Enter full name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="um_field">
              <label>Email</label>
              <input
                type="email"
                placeholder="Enter email address"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div className="um_field">
              <label>Role</label>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
              >
                <option value="exam_officer">Exam Officer</option>
                <option value="dean">Dean</option>
              </select>
            </div>
            <button type="submit" className="um_btn" disabled={submitting}>
              {submitting ? "Creating..." : "Create User"}
            </button>
          </form>
        </div>

        <div className="um_list_card">
          <h3>Existing Users</h3>
          {loading && <Loader />}
          {!loading && users.length === 0 && (
            <p className="um_empty">No users created yet.</p>
          )}
          {!loading && users.length > 0 && (
            <table className="um_table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  {isSuperAdmin && <th></th>}
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u._id}>
                    <td>{u.name}</td>
                    <td>{u.email}</td>
                    <td>
                      <span className={`um_role_badge ${u.role}`}>
                        {u.role.replace("_", " ")}
                      </span>
                    </td>
                    {isSuperAdmin && (
                      <td>
                        <button
                          className="um_delete_btn"
                          onClick={() => handleDelete(u._id)}
                        >
                          Delete
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default UserManagement;
